/**
 * Perfil de entrada do dispositivo (mouse/toque) e densidade da interface.
 *
 * Largura de viewport NÃO diz se a máquina é um celular: um monitor de 24"
 * ligado por VGA pode reportar 1024px e um desktop com zoom 50% reporta
 * 1400px+. Aqui usamos as Interaction Media Features (`pointer`, `hover`):
 * quem tem mouse e hover continua com layout/densidade de desktop em qualquer
 * resolução; só ponteiro grosso (toque) recebe alvos maiores.
 */
import { useEffect, useState } from 'react';

export type PointerProfile = 'fine' | 'coarse' | 'unknown';
export type DialerDensity = 'auto' | 'compact' | 'comfortable';
export type ResolvedDensity = 'compact' | 'comfortable';

export interface InputProfile {
  pointer: PointerProfile;
  hover: boolean;
  /** Dispositivo "de toque": ponteiro grosso, ou sem hover e sem ponteiro fino. */
  touchLike: boolean;
}

const QUERIES = {
  fine: '(pointer: fine)',
  coarse: '(pointer: coarse)',
  hover: '(hover: hover)',
} as const;

function matches(query: string): boolean | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  try {
    return window.matchMedia(query).matches;
  } catch {
    return null;
  }
}

export function getInputProfile(): InputProfile {
  const fine = matches(QUERIES.fine);
  const coarse = matches(QUERIES.coarse);
  const hover = matches(QUERIES.hover);

  const pointer: PointerProfile = coarse ? 'coarse' : fine ? 'fine' : 'unknown';
  const touchLike = pointer === 'coarse' || (pointer !== 'fine' && hover === false);

  return { pointer, hover: hover === true, touchLike };
}

/** Densidade efetiva: compacta com mouse/hover, confortável (alvos de toque) em dispositivos de toque. */
export function resolveDensity(density: DialerDensity | undefined, profile: InputProfile): ResolvedDensity {
  if (density === 'compact' || density === 'comfortable') {
    return density;
  }
  return profile.touchLike ? 'comfortable' : 'compact';
}

export function parseDensity(value: string | null | undefined): DialerDensity | undefined {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'compact' || normalized === 'compacta' || normalized === 'compacto') return 'compact';
  if (normalized === 'comfortable' || normalized === 'confortavel' || normalized === 'confortável' || normalized === 'touch') return 'comfortable';
  if (normalized === 'auto') return 'auto';
  return undefined;
}

/** Perfil de entrada reativo (acompanha mudanças, ex.: conectar um mouse a um tablet). */
export function useInputProfile(): InputProfile {
  const [profile, setProfile] = useState<InputProfile>(() => getInputProfile());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const update = () => setProfile(getInputProfile());
    const lists: MediaQueryList[] = [];
    for (const query of Object.values(QUERIES)) {
      try {
        const list = window.matchMedia(query);
        if (typeof list.addEventListener === 'function') {
          list.addEventListener('change', update);
        } else if (typeof list.addListener === 'function') {
          list.addListener(update);
        }
        lists.push(list);
      } catch {
        // ignore
      }
    }
    update();
    return () => {
      for (const list of lists) {
        if (typeof list.removeEventListener === 'function') {
          list.removeEventListener('change', update);
        } else if (typeof list.removeListener === 'function') {
          list.removeListener(update);
        }
      }
    };
  }, []);

  return profile;
}
